import {CaptchaWithSubmitButton} from '@shared/Captcha/Client/CaptchaWithSubmitButton';
import {Button} from '@shared/FormsComponents/Button/Button';
import {Checkbox} from '@shared/FormsComponents/Checkbox/Checkbox';
import {FileUpload} from '@shared/FormsComponents/FileUpload/FileUpload';
import {FormField} from '@shared/FormsComponents/FormField/FormField';
import {getDefaultFormValues} from '@utils/forms/getDefaultFormValues';
import {useSearchParams} from 'next/navigation';
import {useCallback, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';

import {CaptchaTokenPayload} from '@shared/Captcha/types';
import {
  HelpFormItemType,
  HelpFormRequestType,
  WriteSupportForm,
} from '@shared/SupportModal/types';

import {useLocale} from '@utils/hooks/useLocale';

import styles from './HelpForm.module.scss';

type Props = {
  acceptForm: (data: HelpFormRequestType) => void;
  onError: () => void;
  formData: WriteSupportForm;
  fileUploadApiUrl: string;
};

export const HelpForm = ({
  acceptForm,
  onError,
  formData: {
    formItems,
    terms,
    uploadButtonText,
    fileExtensions,
    submitButtonText,
    errorText,
  },
  fileUploadApiUrl,
}: Props) => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const locale = useLocale();

  const toggleTerms = useCallback(
    () => setTermsAccepted((current) => !current),
    [],
  );

  const formDefaultValues = getDefaultFormValues(Object.values(formItems));

  const form = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      ...formDefaultValues,
      fileUpload: [],
    },
    disabled: formDisabled,
  });

  const onSubmitForm = (
    data: HelpFormItemType,
    captchaResult: CaptchaTokenPayload,
  ) => {
    const {fileUpload, ...formData} = data;

    const files = fileUpload
      .filter((item) => item?.url?.length)
      .map((item) => item?.url);

    acceptForm({
      data: {fileUpload: files, ...formData},
      captchaResult,
    });
  };

  const isFormValid = Object.keys(form.formState.errors).length < 1;

  const reasonFieldError =
    Object.keys(form.formState.errors)?.[0] ===
    Object.values(formItems)?.[0]?.inputName;

  const params = useSearchParams();
  const isAutotestRun = params?.has('autotest');

  return (
    <FormProvider {...form}>
      <form data-testid={'help_form_submit_id'}>
        <div className={styles.form}>
          {Object.values(formItems).map((elem) => (
            <FormField
              key={elem.id}
              formItem={elem}
              control={form.control}
              className={styles.formItem}
            />
          ))}
        </div>

        <FileUpload
          title={uploadButtonText}
          fileExtensions={fileExtensions}
          fileUploadApiUrl={fileUploadApiUrl}
        />

        <Checkbox
          termsAccepted={termsAccepted}
          toggleTerms={toggleTerms}
          content={Object.values(terms)?.[0]?.content}
        />

        <CaptchaWithSubmitButton<HelpFormItemType>
          captchaLanguage={locale === 'ru' ? 'ru' : 'en'}
          placement="help_form"
          formValidationMode="after_click"
          onDisableForm={() => setFormDisabled(true)}
          onCaptchaError={onError}
          renderSubmitButton={({disabled, ...rest}) => (
            <>
              {(!isFormValid || reasonFieldError) && (
                <div className={styles.errorText}>{errorText}</div>
              )}

              <Button
                className={styles.submit}
                disabled={!isAutotestRun && (disabled || !termsAccepted)}
                data-testid={'form_submit_button_id'}
                {...rest}
              >
                {submitButtonText}
              </Button>
            </>
          )}
          onSubmitForm={onSubmitForm}
        />
      </form>
    </FormProvider>
  );
};
